import React, { useEffect, useRef, useState } from 'react';
import config from '../../config';
import { MapPicker } from '../../components/MapPicker';
import { jobLeadService } from '../../services/jobLeadService';
import { useAuthStore } from '../../store/authStore';
import {
  JobLead,
  JobLeadStatus,
  SURVEY_SERVICE_TYPES,
} from '../../types';
import { getSurveyorProfile, isSurveyorApproved } from '../../utils/surveyorApproval';

/* ── Status badge colour map ─────────────────────────────── */
const STATUS_STYLE: Record<JobLeadStatus, { bg: string; color: string; label: string }> = {
  [JobLeadStatus.PENDING_APPROVAL]: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)', label: '🟡 Review' },
  [JobLeadStatus.APPROVED]:         { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', label: '🟢 Approved' },
  [JobLeadStatus.REJECTED]:         { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)', label: '🔴 Rejected' },
  [JobLeadStatus.FULFILLED]:        { bg: 'rgba(139, 92, 246, 0.15)', color: '#8B5CF6', label: '🟢 Fulfilled' },
};

const DEFAULT_FORM = {
  service_type: SURVEY_SERVICE_TYPES[0] as string,
  client_name: '',
  client_phone: '+263',
  location_address: '',
  latitude: null as number | null,
  longitude: null as number | null,
  description: '',
  estimated_value: '',
  requested_referrer_share_pct: '30',
};

const platformCommissionRate = config.DEFAULT_COMMISSION_RATE;

export const SurveyorAdvertiseJob: React.FC = () => {
  const { user } = useAuthStore();
  const profile = getSurveyorProfile(user);
  const canAdvertise = isSurveyorApproved(user);

  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const [leads, setLeads] = useState<JobLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<JobLeadStatus | 'ALL'>('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement>(null);

  const set = (k: string, v: string | number | null) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const res = await jobLeadService.listMine();
      setLeads(res.results ?? []);
    } catch {
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  };

  // ── Suspension gate ──────────────────────────────────────
  if (profile?.is_suspended) {
    return (
      <div style={s.centred}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#DC2626', margin: '0 0 8px' }}>Account Suspended</h2>
        <p style={{ fontSize: 14, color: '#636E72', maxWidth: 420, lineHeight: 1.6 }}>
          Advertising job leads is disabled while your account is suspended. Contact admin to resolve.
        </p>
        {profile.suspension_reason && (
          <div style={s.rejectBox}>Reason: {profile.suspension_reason}</div>
        )}
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    if (!canAdvertise) {
      setSubmitError('Your account is waiting for admin approval. You can browse jobs and view referrals, but advertising jobs is enabled only after approval.');
      return;
    }
    if (!form.client_name.trim()) { setSubmitError('Client name is required.'); return; }
    if (!form.client_phone.trim() || form.client_phone.length < 9) { setSubmitError('Please enter a valid client phone number.'); return; }
    if (!form.location_address.trim()) { setSubmitError('Location / address is required.'); return; }
    if (form.latitude === null || form.longitude === null) { setSubmitError('Search and select the job location on the map.'); return; }
    const requestedShare = Number(form.requested_referrer_share_pct);
    if (!Number.isFinite(requestedShare) || requestedShare < 0 || requestedShare > 100) {
      setSubmitError('Requested commission share must be between 0 and 100%.');
      return;
    }
    if (!form.description.trim() || form.description.length < 20) { setSubmitError('Please provide a description of at least 20 characters.'); return; }

    setSubmitting(true);
    try {
      await jobLeadService.create({
        service_type: form.service_type,
        client_name: form.client_name.trim(),
        client_phone: form.client_phone.trim(),
        location_address: form.location_address.trim(),
        latitude: form.latitude,
        longitude: form.longitude,
        description: form.description.trim(),
        estimated_value: form.estimated_value ? Number(form.estimated_value) : undefined,
        requested_referrer_share_pct: requestedShare,
      });
      setSubmitSuccess('Job lead submitted! Admin will review it shortly.');
      setForm({ ...DEFAULT_FORM });
      loadLeads();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resubmitLead = async (leadId: string) => {
    setSubmitError('');
    setSubmitSuccess('');
    try {
      await jobLeadService.resubmit(leadId);
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? { ...lead, status: JobLeadStatus.PENDING_APPROVAL, rejection_reason: '' }
            : lead
        )
      );
      setSubmitSuccess('Job lead resubmitted. Admin will review it again.');
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || 'Failed to resubmit job lead.');
    }
  };

  const filtered = filterStatus === 'ALL' ? leads : leads.filter((l) => l.status === filterStatus);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short', year: 'numeric' });

  /* ── Payout preview helper ───────────────────────────── */
  const calcPayout = (lead: JobLead) => {
    if (!lead.estimated_value) return null;
    const platformCommission = lead.estimated_value * (lead.platform_commission_rate ?? platformCommissionRate);
    const referrerPayout = (platformCommission * lead.referrer_share_pct) / 100;
    return { platformCommission, referrerPayout };
  };

  return (
    <div style={s.page}>
      {/* ════ HEADER ════ */}
      <div style={s.header}>
        <div>
          <h1 style={s.pageTitle}>Advertise a Job Lead</h1>
          <p style={s.pageSubtitle}>
            Found a potential survey job in your area? Submit it here. Once admin approves, you earn a share of the platform commission when the deal closes.
          </p>
        </div>
        <div style={s.statsRow}>
          {[
            { label: 'Submitted', value: leads.length, color: '#6366F1' },
            { label: 'Approved', value: leads.filter((l) => l.status === JobLeadStatus.APPROVED).length, color: '#10B981' },
            { label: 'Fulfilled', value: leads.filter((l) => l.status === JobLeadStatus.FULFILLED).length, color: '#7C3AED' },
          ].map((stat) => (
            <div key={stat.label} style={s.statPill}>
              <span style={{ ...s.statNum, color: stat.color }}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={s.layout}>
        {/* ════ LEFT — Submit Form ════ */}
        <div ref={formRef} style={s.formCard}>
          <h2 style={s.sectionTitle}>{canAdvertise ? 'Submit New Lead' : 'Approval Required'}</h2>

          {submitSuccess && (
            <div style={s.successBox}>{submitSuccess}</div>
          )}
          {submitError && (
            <div style={s.errorBox}>{submitError}</div>
          )}

          {!canAdvertise ? (
            <div style={s.infoBanner}>
              Your account is waiting for admin approval. You can browse jobs and view referrals, but advertising jobs is enabled only after approval.
            </div>
          ) : (
          <form onSubmit={handleSubmit} style={s.form}>
            {/* Info banner */}
            <div style={s.infoBanner}>
              <strong>How it works:</strong> After approval, admin sets the profit split (e.g. you earn 30% of the platform commission). When the job is completed and the deal closes, your payout is credited automatically.
            </div>

            <label style={s.label}>Service Type <span style={s.req}>*</span></label>
            <select
              value={form.service_type}
              onChange={(e) => set('service_type', e.target.value)}
              style={s.select}
              required
            >
              {SURVEY_SERVICE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <div style={s.row}>
              <div style={s.col}>
                <label style={s.label}>Client Name <span style={s.req}>*</span></label>
                <input
                  value={form.client_name}
                  onChange={(e) => set('client_name', e.target.value)}
                  style={s.input}
                  placeholder="e.g. Chipo Mutasa"
                  required
                />
              </div>
              <div style={s.col}>
                <label style={s.label}>Client Phone <span style={s.req}>*</span></label>
                <input
                  type="tel"
                  value={form.client_phone}
                  onChange={(e) => set('client_phone', e.target.value)}
                  style={s.input}
                  placeholder="+263 7X XXX XXXX"
                  required
                />
                <span style={s.helpText}>Visible to admin and you only.</span>
              </div>
            </div>

            <label style={s.label}>Location / Address <span style={s.req}>*</span></label>
            <MapPicker
              latitude={form.latitude}
              longitude={form.longitude}
              height={260}
              onChange={(lat, lng) => {
                set('latitude', lat);
                set('longitude', lng);
              }}
              onSelectAddress={(address) => set('location_address', address)}
            />
            <input
              value={form.location_address}
              onChange={(e) => set('location_address', e.target.value)}
              style={{ ...s.input, marginTop: 8 }}
              placeholder="Selected map location address"
              required
            />

            <label style={s.label}>Estimated Job Value (USD)</label>
            <input
              type="number"
              min="0"
              step="50"
              value={form.estimated_value}
              onChange={(e) => set('estimated_value', e.target.value)}
              style={s.input}
              placeholder="e.g. 1200"
            />

            <label style={s.label}>Your Requested Commission Share %</label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.requested_referrer_share_pct}
              onChange={(e) => set('requested_referrer_share_pct', e.target.value)}
              style={s.input}
              placeholder="e.g. 30"
            />
            {form.estimated_value && Number(form.estimated_value) > 0 && (
              <div style={s.payoutPreview}>
                Platform commission at {(platformCommissionRate * 100).toFixed(0)}%:{' '}
                <strong>${(Number(form.estimated_value) * platformCommissionRate).toFixed(2)}</strong>
                {' '}· Your requested share ({Number(form.requested_referrer_share_pct || 0).toFixed(0)}%):{' '}
                <strong style={{ color: '#7C3AED' }}>
                  ${(Number(form.estimated_value) * platformCommissionRate * (Number(form.requested_referrer_share_pct || 0) / 100)).toFixed(2)}
                </strong>
              </div>
            )}

            <label style={s.label}>Description <span style={s.req}>*</span></label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              style={{ ...s.input, minHeight: 100, resize: 'vertical' as const }}
              placeholder="Describe the job: what the client needs, site conditions, urgency, etc."
              required
            />

            <button type="submit" style={s.submitBtn} disabled={submitting}>
              {submitting ? 'Submitting…' : '📢 Submit Job Lead'}
            </button>
          </form>
          )}
        </div>

        {/* ════ RIGHT — Lead History ════ */}
        <div style={s.listCard}>
          <div style={s.listHeader}>
            <h2 style={s.sectionTitle}>My Submitted Leads</h2>
            <div style={s.filterRow}>
              {(['ALL', ...Object.values(JobLeadStatus)] as const).map((st) => (
                <button
                  key={st}
                  style={{ ...s.filterTab, ...(filterStatus === st ? s.filterTabActive : {}) }}
                  onClick={() => setFilterStatus(st)}
                >
                  {st === 'ALL' ? 'All' : STATUS_STYLE[st as JobLeadStatus].label.replace(/^[^a-zA-Z]+/, '')}
                </button>
              ))}
            </div>
          </div>

          {leadsLoading ? (
            <div style={s.centredInner}><div style={s.spinner} /></div>
          ) : filtered.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
              <p>No leads {filterStatus !== 'ALL' ? `with status "${filterStatus.replace(/_/g, ' ')}"` : 'submitted yet'}.</p>
            </div>
          ) : (
            <div style={s.leadList}>
              {filtered.map((lead) => {
                const st = STATUS_STYLE[lead.status];
                const isExpanded = expandedId === lead.id;
                const payout = calcPayout(lead);
                return (
                  <div key={lead.id} style={s.leadCard}>
                    {/* Card header */}
                    <div style={s.leadCardTop} onClick={() => setExpandedId(isExpanded ? null : lead.id)}>
                      <div style={s.leadLeft}>
                        <span style={{ ...s.statusBadge, background: st.bg, color: st.color }}>{st.label}</span>
                        <strong style={s.leadType}>{lead.service_type}</strong>
                        <span style={s.leadAddress}>{lead.location_address}</span>
                      </div>
                      <div style={s.leadRight}>
                        {lead.estimated_value && (
                          <span style={s.leadValue}>${lead.estimated_value.toLocaleString()}</span>
                        )}
                        <span style={s.leadDate}>{formatDate(lead.created_at)}</span>
                        <span style={s.chevron}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={s.leadDetail}>
                        <div style={s.detailGrid}>
                          <div style={s.detailItem}>
                            <span style={s.detailLabel}>Client</span>
                            <span style={s.detailVal}>{lead.client_name}</span>
                          </div>
                          <div style={s.detailItem}>
                            <span style={s.detailLabel}>Client Phone</span>
                            <span style={s.detailVal}>{lead.client_phone}</span>
                          </div>
                          <div style={s.detailItem}>
                            <span style={s.detailLabel}>Requested Share</span>
                            <span style={s.detailVal}>{lead.requested_referrer_share_pct}%</span>
                          </div>
                          <div style={s.detailItem}>
                            <span style={s.detailLabel}>Your Share</span>
                            <span style={{ ...s.detailVal, color: '#7C3AED', fontWeight: 700 }}>
                              {lead.status === JobLeadStatus.PENDING_APPROVAL ? 'Pending admin decision' : `${lead.referrer_share_pct}%`}
                            </span>
                          </div>
                          <div style={s.detailItem}>
                            <span style={s.detailLabel}>Admin Share</span>
                            <span style={s.detailVal}>
                              {lead.status === JobLeadStatus.PENDING_APPROVAL ? '—' : `${lead.admin_share_pct}%`}
                            </span>
                          </div>
                        </div>

                        {payout && lead.status !== JobLeadStatus.PENDING_APPROVAL && (
                          <div style={s.payoutRow}>
                            <span>Est. platform commission: <strong>${payout.platformCommission.toFixed(2)}</strong></span>
                            <span>Your payout ({lead.referrer_share_pct}%): <strong style={{ color: '#7C3AED' }}>${payout.referrerPayout.toFixed(2)}</strong></span>
                          </div>
                        )}

                        {lead.status === JobLeadStatus.FULFILLED && lead.payout_amount !== undefined && (
                          <div style={s.fulfilledBanner}>
                            🎉 Deal closed! Your referral payout: <strong>${lead.payout_amount.toFixed(2)}</strong>
                          </div>
                        )}

                        {lead.status === JobLeadStatus.REJECTED && lead.rejection_reason && (
                          <div style={s.rejectBox}>Rejection reason: {lead.rejection_reason}</div>
                        )}

                        {lead.status === JobLeadStatus.REJECTED && (
                          <button type="button" style={{ ...s.submitBtn, marginTop: 10 }} onClick={() => resubmitLead(lead.id)}>
                            Resubmit for Review
                          </button>
                        )}

                        {lead.notes && (
                          <div style={s.noteBox}>Admin note: {lead.notes}</div>
                        )}

                        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--sc-text-primary)', lineHeight: 1.6 }}>
                          {lead.description}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Styles ─────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  page: { padding: '16px', fontFamily: "'Inter', sans-serif", maxWidth: 1300, margin: '0 auto', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  pageTitle: { fontSize: 22, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px' },
  pageSubtitle: { fontSize: 13, color: 'var(--sc-text-secondary)', maxWidth: 580, lineHeight: 1.6, margin: 0 },
  statsRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  statPill: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '12px 18px', boxShadow: 'var(--sc-shadow-xs)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, border: '1px solid var(--sc-border)', boxSizing: 'border-box' },
  statNum: { fontSize: 20, fontWeight: 800 },
  statLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500, marginTop: 2 },

  layout: { display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' },
  formCard: { flex: '1 1 300px', maxWidth: '100%', background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: '16px 14px', boxShadow: 'var(--sc-shadow-sm)', border: '1px solid var(--sc-border)', boxSizing: 'border-box' },
  listCard: { flex: '1 1 320px', width: '100%', minWidth: 0, boxSizing: 'border-box' },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px' },

  infoBanner: { background: 'var(--sc-info-bg)', border: '1px solid var(--sc-info-border)', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: 'var(--sc-info-text)', lineHeight: 1.6, marginBottom: 14 },
  successBox: { background: 'var(--sc-success-bg)', border: '1px solid var(--sc-success-border)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--sc-success-text)', marginBottom: 12 },
  errorBox: { background: 'var(--sc-error-bg)', border: '1px solid var(--sc-error-border)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--sc-error-text)', marginBottom: 12 },

  form: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap' as const },
  col: { flex: '1 1 180px', display: 'flex', flexDirection: 'column' },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)', marginBottom: 5, marginTop: 12 },
  req: { color: 'var(--sc-error)' },
  input: { height: 48, padding: '0 12px', border: '1.5px solid var(--sc-border)', borderRadius: 10, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', outline: 'none', background: 'var(--sc-bg-secondary)', boxSizing: 'border-box' },
  helpText: { fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 4 },
  select: { height: 48, padding: '0 12px', border: '1.5px solid var(--sc-border)', borderRadius: 10, fontSize: 13, fontFamily: "'Inter', sans-serif", color: 'var(--sc-text-primary)', background: 'var(--sc-bg-secondary)', outline: 'none', boxSizing: 'border-box' },
  payoutPreview: { fontSize: 12, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-primary)', borderRadius: 8, padding: '7px 11px', marginTop: 6 },
  submitBtn: { marginTop: 20, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' },

  listHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 },
  filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filterTab: { padding: '5px 12px', borderRadius: 20, border: '1px solid var(--sc-border)', background: 'var(--sc-bg-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: 'var(--sc-text-secondary)', fontFamily: "'Inter', sans-serif" },
  filterTabActive: { background: 'var(--sc-brand-primary)', borderColor: 'var(--sc-brand-primary)', color: 'var(--sc-text-on-brand)' },
  leadList: { display: 'flex', flexDirection: 'column', gap: 10 },
  leadCard: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-xs)', boxSizing: 'border-box' },
  leadCardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', gap: 10 },
  leadLeft: { display: 'flex', flexDirection: 'column', gap: 3 },
  leadRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 },
  statusBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  leadType: { fontSize: 14, color: 'var(--sc-text-primary)' },
  leadAddress: { fontSize: 12, color: 'var(--sc-text-secondary)' },
  leadValue: { fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)' },
  leadDate: { fontSize: 11, color: 'var(--sc-text-secondary)' },
  chevron: { fontSize: 10, color: 'var(--sc-text-secondary)', marginTop: 2 },
  leadDetail: { borderTop: '1px solid var(--sc-border)', padding: '10px 12px', background: 'var(--sc-bg-primary)', boxSizing: 'border-box' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px 16px', marginBottom: 12 },
  detailItem: { display: 'flex', flexDirection: 'column' },
  detailLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500 },
  detailVal: { fontSize: 13, color: 'var(--sc-text-primary)', fontWeight: 500, marginTop: 2 },
  payoutRow: { display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 8, padding: '8px 12px', marginBottom: 10 },
  fulfilledBanner: { background: 'var(--sc-success-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--sc-success-text)', marginBottom: 10 },
  rejectBox: { background: 'var(--sc-error-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--sc-error-text)', marginBottom: 10 },
  noteBox: { background: 'var(--sc-warning-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--sc-warning-text)', marginBottom: 10 },
  empty: { textAlign: 'center', padding: '48px 20px', color: 'var(--sc-text-secondary)', fontSize: 14 },
  centred: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: 40 },
  centredInner: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },
};
